import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  FileText,
  CreditCard,
  Receipt,
  Save,
  Shield,
  Image as ImageIcon,
  AlertTriangle,
  Upload,    
  Trash2,
} from 'lucide-react';

const CompanySettingsPage = () => {
  const { token, isAdmin } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    company_name: '',
    legal_form: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    email: '',
    phone: '',
    website: '',
    siret: '',
    rcs: '',
    vat_number: '',
    is_vat_subject: true,
    default_vat_rate: 20,
    bank_details: {
      iban: '',
      bic: '',
      bank_name: '',
    },
    // Modification: On stocke la chaîne Base64 ici
    logo_base64: '', 
    primary_color: '#4f46e5',
    legal_mentions_default: '',
    quote_validity_days: 30,
    payment_terms: '',
  });

  // Check admin access
  const userIsAdmin = isAdmin();
  
  const { data: settings, isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch settings');
      return res.json();
    },
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        company_name: settings.company_name || '',
        legal_form: settings.legal_form || '',
        address: settings.address || '',
        city: settings.city || '',
        postal_code: settings.postal_code || '',
        country: settings.country || 'France',
        email: settings.email || '',
        phone: settings.phone || '',
        website: settings.website || '',
        siret: settings.siret || '',
        rcs: settings.rcs || '',
        vat_number: settings.vat_number || '',
        is_vat_subject: settings.is_vat_subject ?? true,
        default_vat_rate: settings.default_vat_rate || 20,
        bank_details: settings.bank_details || { iban: '', bic: '', bank_name: '' },
        logo_base64: settings.logo_base64 || '', // Charge le base64 existant
        primary_color: settings.primary_color || '#4f46e5',
        legal_mentions_default: settings.legal_mentions_default || '',
        quote_validity_days: settings.quote_validity_days || 30,
        payment_terms: settings.payment_terms || '',
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/settings/company`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update settings');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      toast.success('Paramètres enregistrés');
    },
    onError: (err) => toast.error(err.message),
  });

  // --- NOUVELLE FONCTION: Conversion en Base64 côté client ---
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image valide');
      return;
    }
    
    // Limite taille (ex: 500 Ko pour base64 c'est bien)
    if (file.size > 500 * 1024) {
      toast.error("L'image est trop volumineuse pour être stockée (max 500 Ko)");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      // Le résultat est une string "data:image/png;base64,....."
      const base64String = reader.result;
      updateField('logo_base64', base64String);
      toast.success("Logo prêt à être enregistré");
    };
    reader.onerror = () => {
      toast.error("Erreur lors de la lecture du fichier");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateBankDetails = (field, value) => {
    setFormData(prev => ({
      ...prev,
      bank_details: { ...prev.bank_details, [field]: value }
    }));
  };

  if (!userIsAdmin) {
    return (
      <div className="space-y-6" data-testid="settings-restricted">
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Accès restreint</h3>
            <p className="text-muted-foreground">
              Seuls les administrateurs peuvent modifier les paramètres de l'entreprise.
            </p>
            <Button onClick={() => navigate('/')} className="mt-4">
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Paramètres Entreprise</h1>
          <p className="text-muted-foreground">Configurez les informations de votre société pour les documents légaux</p>
        </div>
        <Button onClick={handleSubmit} disabled={updateMutation.isPending} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="identity" className="gap-2">
              <Building2 className="w-4 h-4 hidden sm:block" /> Identité
            </TabsTrigger>
            <TabsTrigger value="legal" className="gap-2">
              <FileText className="w-4 h-4 hidden sm:block" /> Légal
            </TabsTrigger>
            <TabsTrigger value="banking" className="gap-2">
              <CreditCard className="w-4 h-4 hidden sm:block" /> Banque
            </TabsTrigger>
            <TabsTrigger value="quotes" className="gap-2">
              <Receipt className="w-4 h-4 hidden sm:block" /> Devis
            </TabsTrigger>
          </TabsList>

          {/* IDENTITY TAB */}
          <TabsContent value="identity">
            <Card>
              <CardHeader>
                <CardTitle>Identité de l'entreprise</CardTitle>
                <CardDescription>Ces informations apparaîtront sur vos devis et factures</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nom commercial *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder="Ma Super Entreprise"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal_form">Forme juridique</Label>
                    <Input
                      id="legal_form"
                      value={formData.legal_form}
                      onChange={(e) => updateField('legal_form', e.target.value)}
                      placeholder="SARL, SAS, Auto-entrepreneur..."
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Rue de l'Exemple"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Code postal</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="75001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Ville</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Paris"
                    />
                  </div>
                  <div className="space-y-2 col-span-2 md:col-span-1">
                    <Label htmlFor="country">Pays</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => updateField('country', e.target.value)}
                      placeholder="France"
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="contact@monentreprise.fr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Site web</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://www.monentreprise.fr"
                  />
                </div>

                <Separator />

                {/* --- SECTION LOGO REVUE (BASE64) --- */}
                <div className="space-y-3">
                  <Label>Logo de l'entreprise</Label>
                  
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    {/* Prévisualisation */}
                    <div className="shrink-0">
                      <div className="relative w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden group">
                        {formData.logo_base64 ? (
                          <>
                            <img 
                              src={formData.logo_base64} 
                              alt="Logo" 
                              className="w-full h-full object-contain p-2"
                              onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = "https://placehold.co/150x150?text=Erreur+Image";
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => updateField('logo_base64', '')}
                              className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              title="Supprimer le logo"
                            >
                              <Trash2 className="w-6 h-6" />
                            </button>
                          </>
                        ) : (
                          <div className="text-center text-muted-foreground p-2">
                            <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
                            <span className="text-xs">Aucun logo</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contrôles */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label htmlFor="file-upload" className="mb-2 block text-sm font-medium">
                          Importer une image
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="file-upload"
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={handleLogoChange}
                            className="hidden" 
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => document.getElementById('file-upload').click()}
                            className="w-full sm:w-auto"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Choisir un fichier
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG ou JPG recommandé. Max 500 Ko (stocké en base).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* --- FIN SECTION LOGO --- */}

                <div className="space-y-2">
                  <Label htmlFor="primary_color">Couleur principale</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="primary_color"
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => updateField('primary_color', e.target.value)}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => updateField('primary_color', e.target.value)}
                      placeholder="#4f46e5"
                      className="w-32"
                    />
                    <span className="text-sm text-muted-foreground">Pour l'en-tête des PDF</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LEGAL TAB (Reste inchangé) */}
          <TabsContent value="legal">
            <Card>
              <CardHeader>
                <CardTitle>Informations légales</CardTitle>
                <CardDescription>Identifiants légaux obligatoires sur les documents commerciaux</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET</Label>
                    <Input
                      id="siret"
                      value={formData.siret}
                      onChange={(e) => updateField('siret', e.target.value)}
                      placeholder="123 456 789 00012"
                      data-testid="siret-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rcs">RCS</Label>
                    <Input
                      id="rcs"
                      value={formData.rcs}
                      onChange={(e) => updateField('rcs', e.target.value)}
                      placeholder="RCS Paris B 123 456 789"
                    />
                  </div>
                </div>

                <Separator />

                <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">Assujetti à la TVA</Label>
                      <p className="text-sm text-muted-foreground">
                        Activez si vous êtes assujetti à la TVA
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_vat_subject}
                      onCheckedChange={(v) => updateField('is_vat_subject', v)}
                      data-testid="vat-toggle"
                    />
                  </div>

                  {!formData.is_vat_subject && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-600">Mode Auto-entrepreneur / Non assujetti</p>
                        <p className="text-xs text-muted-foreground">
                          Vos devis afficheront "TVA non applicable, art. 293 B du CGI" et n'auront pas de colonne TVA.
                        </p>
                      </div>
                    </div>
                  )}

                  {formData.is_vat_subject && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="vat_number">N° TVA Intracommunautaire</Label>
                        <Input
                          id="vat_number"
                          value={formData.vat_number}
                          onChange={(e) => updateField('vat_number', e.target.value)}
                          placeholder="FR12 345678901"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="default_vat_rate">Taux de TVA par défaut (%)</Label>
                        <Input
                          id="default_vat_rate"
                          type="number"
                          step="0.1"
                          value={formData.default_vat_rate}
                          onChange={(e) => updateField('default_vat_rate', parseFloat(e.target.value) || 20)}
                          placeholder="20"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_mentions_default">Mentions légales par défaut</Label>
                  <Textarea
                    id="legal_mentions_default"
                    value={formData.legal_mentions_default}
                    onChange={(e) => updateField('legal_mentions_default', e.target.value)}
                    placeholder="Pénalités de retard: 3 fois le taux d'intérêt légal. Indemnité forfaitaire de recouvrement: 40€."
                    rows={3}
                    data-testid="legal-mentions-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce texte apparaîtra en bas de tous vos devis
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* BANKING TAB */}
          <TabsContent value="banking">
            <Card>
              <CardHeader>
                <CardTitle>Coordonnées bancaires</CardTitle>
                <CardDescription>Ces informations apparaîtront sur vos devis pour faciliter le règlement</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Nom de la banque</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_details.bank_name}
                    onChange={(e) => updateBankDetails('bank_name', e.target.value)}
                    placeholder="BNP Paribas"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN</Label>
                  <Input
                    id="iban"
                    value={formData.bank_details.iban}
                    onChange={(e) => updateBankDetails('iban', e.target.value)}
                    placeholder="FR76 3000 4008 0000 0012 3456 789"
                    className="font-mono"
                    data-testid="iban-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bic">BIC / SWIFT</Label>
                  <Input
                    id="bic"
                    value={formData.bank_details.bic}
                    onChange={(e) => updateBankDetails('bic', e.target.value)}
                    placeholder="BNPAFRPPXXX"
                    className="font-mono w-48"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QUOTES TAB */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres des devis</CardTitle>
                <CardDescription>Configurez les valeurs par défaut pour vos devis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="quote_validity_days">Validité des devis (jours)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="quote_validity_days"
                      type="number"
                      min="1"
                      max="365"
                      value={formData.quote_validity_days}
                      onChange={(e) => updateField('quote_validity_days', parseInt(e.target.value) || 30)}
                      className="w-24"
                      data-testid="validity-days-input"
                    />
                    <span className="text-sm text-muted-foreground">jours à partir de la date de création</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Conditions de règlement par défaut</Label>
                  <Textarea
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => updateField('payment_terms', e.target.value)}
                    placeholder="Acompte de 30% à la commande, solde à la livraison."
                    rows={2}
                    data-testid="payment-terms-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Peut être personnalisé pour chaque devis individuellement
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </form>

      {/* Preview section */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-lg">Aperçu</CardTitle>
          <CardDescription>Voici comment vos informations apparaîtront sur les devis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white dark:bg-slate-900 p-6 rounded-lg border shadow-sm">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <p className="font-bold text-lg">{formData.company_name || 'Nom de l\'entreprise'}</p>
                {formData.legal_form && <p className="text-sm text-muted-foreground">{formData.legal_form}</p>}
                {formData.address && <p className="text-sm">{formData.address}</p>}
                {(formData.postal_code || formData.city) && (
                  <p className="text-sm">{formData.postal_code} {formData.city}</p>
                )}
                {formData.siret && <p className="text-xs text-muted-foreground">SIRET: {formData.siret}</p>}
              </div>
              <div className="text-right space-y-1">
                <Badge 
                  variant="outline" 
                  style={{ borderColor: formData.primary_color, color: formData.primary_color }}
                >
                  {formData.is_vat_subject ? 'Assujetti TVA' : 'Non assujetti TVA'}
                </Badge>
              </div>
            </div>
            {formData.bank_details.iban && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  <strong>Paiement:</strong> {formData.bank_details.bank_name} - IBAN: {formData.bank_details.iban}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettingsPage;